import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useStore } from '../../../stores/root';

// assets
import { faHome, faHouseLaptop, faNotesMedical, faPlaneDeparture } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// components
import { Pivot, PivotItem, Spinner, SpinnerSize, Stack } from '@fluentui/react';
import Text from '../../../components/typography/text';
import EmployeeLeavesList from '../../../components/hr/leaves/general/list';
import LeaveMatrix from '../../../components/hr/leaves/matrix';

// services
import EmployeeLeaveService from '../../../services/employees/leave';

// props
import { IEmployeeLeaveBalanceProps } from '../../../props/employees/leaves/leave';

const HRLeavesPage: React.FC = () => {
    const { user, topbar } = useStore();
    const [balance, setBalance] = useState<IEmployeeLeaveBalanceProps | undefined>();
    const [loadingBalance, setLoadingBalance] = useState<boolean>(true);
    const [selectedTab, setSelectedTab] = useState<string>('list');

    useEffect(() => {
        topbar.show('Leaves & WFH', [
            { key: 'home', icon: faHome, href: '/' },
            { key: 'hr', text: 'HR' },
            { key: 'leaves', text: 'Leaves & WFH' }
        ]);
    }, []);

    useEffect(() => {
        loadBalance();
    }, [user.data?.id]);

    const loadBalance = async () => {
        if (!user.data?.id) return;
        try {
            setLoadingBalance(true);
            const bal = await EmployeeLeaveService.getBalance(`employeeId=${user.data.id}&year=${moment().year()}`);
            setBalance(bal);
            setLoadingBalance(false);
        } catch (e) {
            setLoadingBalance(false);
        }
    };

    return (
        <Stack tokens={{ childrenGap: 20 }} styles={{ root: { padding: '24px 32px' } }}>
            {/* Page Header */}
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" wrap tokens={{ childrenGap: 16 }}>
                <div>
                    <Text size="large" style={{ fontWeight: 700, color: '#1e293b' }}>
                        Leaves & WFH Management
                    </Text>
                    <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
                        Digital attendance, automatic annual leave & WFH balance ledger, and multi-tier approval.
                    </div>
                </div>
            </Stack>

            {/* 3 Summary KPI Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: 16
            }}>
                {/* Card 1: Annual Leave */}
                <div style={{
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderLeft: '4px solid #0284c7',
                    borderRadius: 8,
                    padding: '16px 20px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div>
                        <Text size="small" style={{ color: '#64748b', fontWeight: 600 }}>
                            Your Remaining Annual Leave ({moment().year()})
                        </Text>
                        <div style={{ fontSize: 26, fontWeight: 700, color: '#0284c7', marginTop: 4 }}>
                            {loadingBalance ? (
                                <Spinner size={SpinnerSize.small} />
                            ) : (
                                <>
                                    {balance?.annualRemaining ?? 12}
                                    <span style={{ fontSize: 14, fontWeight: 400, color: '#94a3b8' }}>
                                        {' '}/ {balance?.annualQuota ?? 12} Days
                                    </span>
                                </>
                            )}
                        </div>
                        <Text size="xsmall" style={{ color: '#94a3b8', marginTop: 2, display: 'block' }}>
                            Used: {balance?.annualUsed ?? 0} Days (Deducting Quota)
                        </Text>
                    </div>
                    <div style={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        background: '#e0f2fe',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#0284c7',
                        fontSize: 20
                    }}>
                        <FontAwesomeIcon icon={faPlaneDeparture} />
                    </div>
                </div>

                {/* Card 2: WFH Quota */}
                <div style={{
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderLeft: '4px solid #10b981',
                    borderRadius: 8,
                    padding: '16px 20px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div>
                        <Text size="small" style={{ color: '#64748b', fontWeight: 600 }}>
                            Your Remaining WFH Quota ({moment().year()})
                        </Text>
                        <div style={{ fontSize: 26, fontWeight: 700, color: '#10b981', marginTop: 4 }}>
                            {loadingBalance ? (
                                <Spinner size={SpinnerSize.small} />
                            ) : (
                                <>
                                    {balance?.wfhRemaining ?? 3}
                                    <span style={{ fontSize: 14, fontWeight: 400, color: '#94a3b8' }}>
                                        {' '}/ {balance?.wfhQuota ?? 3} Days
                                    </span>
                                </>
                            )}
                        </div>
                        <Text size="xsmall" style={{ color: '#94a3b8', marginTop: 2, display: 'block' }}>
                            Used: {balance?.wfhUsed ?? 0} Days (Deducting Quota)
                        </Text>
                    </div>
                    <div style={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        background: '#dcfce7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#10b981',
                        fontSize: 20
                    }}>
                        <FontAwesomeIcon icon={faHouseLaptop} />
                    </div>
                </div>

                {/* Card 3: Special Leave */}
                <div style={{
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderLeft: '4px solid #8b5cf6',
                    borderRadius: 8,
                    padding: '16px 20px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div>
                        <Text size="small" style={{ color: '#64748b', fontWeight: 600 }}>
                            Special & Sick Leave ({moment().year()})
                        </Text>
                        <div style={{ fontSize: 26, fontWeight: 700, color: '#8b5cf6', marginTop: 4 }}>
                            {loadingBalance ? (
                                <Spinner size={SpinnerSize.small} />
                            ) : (
                                <>{balance?.specialUsed ?? 0} <span style={{ fontSize: 14, fontWeight: 400, color: '#94a3b8' }}>Days</span></>
                            )}
                        </div>
                        <Text size="xsmall" style={{ color: '#94a3b8', marginTop: 2, display: 'block' }}>
                            Statutory / Medical Certificate (No Quota Deduction)
                        </Text>
                    </div>
                    <div style={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        background: '#f3e8ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#8b5cf6',
                        fontSize: 20
                    }}>
                        <FontAwesomeIcon icon={faNotesMedical} />
                    </div>
                </div>
            </div>

            {/* Pivot View */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <Pivot
                    selectedKey={selectedTab}
                    onLinkClick={(item) => {
                        if (item) setSelectedTab(item.props.itemKey || 'list');
                    }}
                    styles={{
                        root: { borderBottom: '1px solid #e2e8f0', marginBottom: 16 }
                    }}
                >
                    <PivotItem headerText="Leave Requests" itemKey="list">
                        <div style={{ marginTop: 12 }}>
                            <EmployeeLeavesList onRefresh={loadBalance} />
                        </div>
                    </PivotItem>
                    <PivotItem headerText="Digital Leave Matrix (Annual)" itemKey="matrix">
                        <div style={{ marginTop: 12 }}>
                            <LeaveMatrix />
                        </div>
                    </PivotItem>
                </Pivot>
            </div>
        </Stack>
    );
};

export default HRLeavesPage;
